import { type FC, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { type Location, type LocationKind } from '../../../models/Scheduling'
import { type LocationInput } from '../../../store/scheduling/dto'
import { formatTimezoneLabel } from './schedulingDisplay'
import { LocationModal } from './LocationModal'
import { LocationImpactDialog, type LocationImpactVariant } from './LocationImpactDialog'

interface PendingLocationChange {
  variant: LocationImpactVariant
  current: Location
  next?: Location
  replacementOptions?: Location[]
  onConfirm: (replacementId?: string) => void
}

interface LocationRowProps {
  location: Location
  badges: string[]
  showSetDefault?: boolean
  onSetDefault?: () => void
  onEdit?: () => void
  onDelete?: () => void
}

const LocationRow: FC<LocationRowProps> = ({ location, badges, showSetDefault = false, onSetDefault, onEdit, onDelete }) => (
  <div className='d-flex align-items-center gap-4 py-3 px-4 border rounded zocks-border-color-gray-300'>
    <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
      <span className='d-flex flex-wrap align-items-center zocks-gap-0.25'>
        <span className='bold zocks-text-gray-900'>{location.name}</span>
        {location.timeZone ? (
          <>
            <span className='zocks-text-gray-700'>·</span>
            <span className='paragraph-small zocks-text-gray-700'>{formatTimezoneLabel(location.timeZone)}</span>
          </>
        ) : null}
      </span>
      <span className='d-flex flex-wrap align-items-center zocks-gap-0.25 paragraph-small zocks-text-gray-700'>
        <span>{location.address}</span>
        {location.note ? (
          <>
            <span>·</span>
            <span>{location.note}</span>
          </>
        ) : null}
      </span>
    </div>
    {badges.length || showSetDefault || onEdit || onDelete ? (
      <div className='d-flex align-items-center gap-3'>
        {badges.map((label) => (
          <span
            key={label}
            className='d-inline-flex align-items-center zocks-gap-0.25 rounded-2 px-2 py-1 paragraph-small zocks-bg-color-primary-light zocks-text-primary-dark'
          >
            <i className='bi bi-star-fill' />
            {label}
          </span>
        ))}
        {showSetDefault && onSetDefault ? (
          <Button variant='borderlessGrayButton' className='p-1' aria-label={`Make ${location.name} your default`} onClick={onSetDefault}>
            <i className='bi bi-star' />
          </Button>
        ) : null}
        {onEdit ? (
          <Button variant='grayButton' className='d-flex align-items-center lh-1 px-3 py-2' aria-label={`Edit ${location.name}`} onClick={onEdit}>
            Edit
          </Button>
        ) : null}
        {onDelete ? (
          <Button variant='grayButton' className='d-flex align-items-center lh-1 px-2 py-2' aria-label={`Delete ${location.name}`} onClick={onDelete}>
            <i className='bi bi-trash' />
          </Button>
        ) : null}
      </div>
    ) : null}
  </div>
)

interface LocationsEditorProps {
  locations: Location[]
  readOnly?: boolean
  title?: string
  description?: string
  defaultLocationId?: string | null
  defaultLabel?: string
  defaultKind?: LocationKind
  accountLocations?: Location[]
  accountDefaultLocationId?: string | null
  onCreate: (input: LocationInput) => void
  onUpdate: (id: string, input: LocationInput) => void
  onDelete: (id: string) => void
  onSetDefault?: (id: string) => void
}

const SubHeader: FC<{ children: string }> = ({ children }) => <div className='lead-small zocks-text-gray-500 text-uppercase'>{children}</div>

export const LocationsEditor: FC<LocationsEditorProps> = ({
  locations,
  readOnly = false,
  title = 'Locations',
  description,
  defaultLocationId,
  defaultLabel = 'Default',
  defaultKind,
  accountLocations,
  accountDefaultLocationId,
  onCreate,
  onUpdate,
  onDelete,
  onSetDefault,
}) => {
  const [editedLocation, setEditedLocation] = useState<Location | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState<PendingLocationChange | null>(null)
  const showGroups = Boolean(accountLocations && accountLocations.length > 0)
  const allLocations = [...(accountLocations ?? []), ...locations]
  const canSetDefault = Boolean(onSetDefault) && !readOnly

  const handleSave = (input: LocationInput) => {
    if (editedLocation) {
      const next: Location = { ...editedLocation, ...input }
      setPendingChange({ variant: 'update', current: editedLocation, next, onConfirm: () => onUpdate(editedLocation.id, input) })
    } else {
      onCreate(input)
    }
    setIsModalOpen(false)
    setEditedLocation(undefined)
  }

  const handleSetDefault = (location: Location) => {
    if (!onSetDefault) {
      return
    }
    const currentDefault = allLocations.find((candidate) => candidate.id === defaultLocationId)
    if (currentDefault) {
      setPendingChange({ variant: 'change-default', current: currentDefault, next: location, onConfirm: () => onSetDefault(location.id) })
    } else {
      onSetDefault(location.id)
    }
  }

  const handleDelete = (location: Location) => {
    setPendingChange({
      variant: 'delete',
      current: location,
      replacementOptions: locations.filter((candidate) => candidate.id !== location.id),
      onConfirm: () => onDelete(location.id),
    })
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h6 className='mb-0 zocks-text-gray-900'>{title}</h6>
          {description ? <span className='paragraph-small zocks-text-gray-700'>{description}</span> : null}
        </div>
        {showGroups ? (
          <div className='d-flex flex-column zocks-gap-0.5'>
            <SubHeader>Account locations</SubHeader>
            <div className='d-flex flex-column zocks-gap-0.5'>
              {(accountLocations ?? []).map((location) => {
                const isYourDefault = location.id === defaultLocationId
                const badges: string[] = []
                if (location.id === accountDefaultLocationId) {
                  badges.push('Account default')
                }
                if (isYourDefault) {
                  badges.push(defaultLabel)
                }
                return <LocationRow key={location.id} location={location} badges={badges} />
              })}
            </div>
          </div>
        ) : null}
        <div className='d-flex flex-column zocks-gap-0.5'>
          {showGroups ? <SubHeader>Your locations</SubHeader> : null}
          {locations.map((location) => {
            const isYourDefault = location.id === defaultLocationId
            return (
              <LocationRow
                key={location.id}
                location={location}
                badges={isYourDefault ? [defaultLabel] : []}
                showSetDefault={canSetDefault && !isYourDefault}
                onSetDefault={canSetDefault ? () => handleSetDefault(location) : undefined}
                onEdit={
                  readOnly
                    ? undefined
                    : () => {
                        setEditedLocation(location)
                        setIsModalOpen(true)
                      }
                }
                onDelete={readOnly ? undefined : () => handleDelete(location)}
              />
            )
          })}
          {locations.length === 0 && !showGroups ? <div className='paragraph-small zocks-text-gray-700'>No locations yet.</div> : null}
        </div>
      </div>
      {!readOnly ? (
        <Button
          variant='primaryLinkButton'
          className='align-self-start p-0 text-decoration-none'
          onClick={() => {
            setEditedLocation(undefined)
            setIsModalOpen(true)
          }}
        >
          <i className='bi bi-plus-circle me-1' />
          Add location
        </Button>
      ) : null}
      {isModalOpen ? (
        <LocationModal key={editedLocation?.id ?? 'new'} location={editedLocation} onCancel={() => setIsModalOpen(false)} onSave={handleSave} />
      ) : null}
      {pendingChange ? (
        <LocationImpactDialog
          variant={pendingChange.variant}
          current={pendingChange.current}
          next={pendingChange.next}
          replacementOptions={pendingChange.replacementOptions}
          defaultKind={defaultKind}
          onConfirm={(replacementId) => {
            pendingChange.onConfirm(replacementId)
            setPendingChange(null)
          }}
          onCancel={() => setPendingChange(null)}
        />
      ) : null}
    </div>
  )
}
